export enum CertificateCreationTriggerEnum {
  ADMIN_CREATE = 'ADMIN_CREATE',
  SALE_WEBHOOK = 'SALE_WEBHOOK',
  ON_OWNER_ACCOUNT_CREATION = 'ON_OWNER_ACCOUNT_CREATION',
  ADMIN_ACTIVATION = 'ADMIN_ACTIVATION',
  CUSTOMER_REACTIVATION = 'CUSTOMER_REACTIVATION',
}

/**
 * When a company's certificates become *active* (and thus billable).
 * Distinct from CertificateCreationTriggerEnum (which gates the *creation*
 * channel). The org-level default lives on
 * certificate_trigger_settings.activation_trigger; per-SKU / per-certificate
 * overrides are a planned follow-up (see the VERA-926 resolved spec).
 */
export enum CertificateActivationTriggerEnum {
  ON_CREATION = 'ON_CREATION',
  ON_PRIMARY_TRANSFER = 'ON_PRIMARY_TRANSFER',
  /**
   * VERA-931 — activate when a location the brand nominated confirms the item
   * arrived. Any address in the address book, not just a shop: the address book
   * already models types (store, production, office), and restricting this to
   * `store` was an arbitrary narrowing that left a brand whose activation moment
   * is arrival at a finishing workshop unable to express it.
   *
   * Renamed from AT_STORE in migration 0101 rather than joined by AT_FACTORY and
   * friends — a family of per-type triggers multiplies forever while the location
   * picker already answers "which one".
   */
  AT_LOCATION = 'AT_LOCATION',
}

import { UUID, CertificateStatus, TCertificateSettings, ExampleKind } from '..';
import { CertificateGroupDivisibility } from './certificate-group-divisibility.enum';

export interface ICertificate {
  /**
   * VRG-361 — null/absent = a real certificate. `demo`/`intro` mark a seeded
   * row. BILLING-CRITICAL: the billing fence (`billableCertificateFence()`) refuses to meter a certificate
   * carrying a value here even when its company is billable, because a demo
   * certificate outlives the prospect's signup inside the same company. See
   * {@link ExampleKind}.
   */
  example_kind?: ExampleKind | null;
  id?: UUID;
  product_id?: UUID | null;
  /**
   * VRG-286 — OUR identifier: uuid FK to `tags.id`. Until
   * drizzle/0063_certificates_tag_id_contract.sql the COLUMN behind this was a `varchar`
   * holding the chip UID, while this type already said `UUID` — part of why the mismatch
   * went unnoticed for so long. Never assign a scanned value to it.
   */
  tag_id?: UUID | null;
  /**
   * VRG-286 — THEIR identifier: the chip's own UID. This is what a scan matches on.
   *
   * (The deprecated `e_tag_id` alias is deliberately NOT declared here. It is a
   * RESPONSE-only compatibility field with no column behind it, so it lives where the other
   * response-only fields already do — the `& {…}` extension on `CertificateEntity.toJSON()`
   * and an optional property on `CertificateDataDto` — rather than becoming part of the
   * canonical certificate shape that every writer has to satisfy.)
   */
  tag_uid?: string | null;
  scan_counter: number | null;
  owner_id?: UUID | null;
  status?: CertificateStatus;
  /**
   * VRG-528 — per-ITEM override of the activation trigger; `null` = inherit from
   * the product, then the company. The most specific level of the chain.
   *
   * Read it through {@link resolveActivationTrigger}, never on its own: a bare
   * read of this field answers "was an override set here", which is a different
   * question from "when does this certificate activate" and is almost never the
   * one a caller wants.
   */
  activation_trigger?: CertificateActivationTriggerEnum | null;
  /**
   * VRG-591 — per-ITEM overrides of the brand's "Certificate access" toggles;
   * `null` = inherit from the product, then the company. The two resolve
   * INDEPENDENTLY of each other.
   *
   * Read them through {@link resolveAccessSettings}, never on their own. A bare
   * read answers "was an override set here", which is a different question from
   * "may this visitor be asked for their email" — and on a privacy control the
   * difference is the whole point, because `false` here is a real answer that a
   * truthiness check would silently discard.
   */
  collect_email?: boolean | null;
  collect_location?: boolean | null;
  meta?: object | null;
  description?: string | null;
  created_at?: Date;
  updated_at?: Date | null;
  date_of_ownership?: Date | null;
  // VERA-924: per-item production date, distinct from the SKU-level
  // `IProduct.manufactured_at` (shared across every certificate of a product).
  manufactured_at?: Date | null;
  /**
   * VRG-231: when this certificate FIRST became active. Write-once — every writer
   * sets it only when it is still null, and nothing clears it, so it survives a
   * lost→found round trip and a deactivation. NULL means NOT RECORDED (rows that
   * activated before the column existed cannot be truthfully backfilled), never
   * "not activated" — see the schema comment on `certificates.activated_at`.
   */
  activated_at?: Date | null;

  /**
   * @deprecated VERA-298 — superseded by `certificate_views`. The column still exists
   * (migration 0046 kept it so a rollback loses nothing) but nothing reads it, and it
   * is no longer emitted on certificate payloads: it was appearing on the PUBLIC
   * response, leaking a brand's visibility config to anonymous scanners.
   */
  settings?: TCertificateSettings | null;
  trigger_event?: CertificateCreationTriggerEnum;
  timestamp?: bigint;
  company_id?: UUID | null;
  public_url?: string | null;
  archived_at?: Date | null;
  archived_by?: string | null;

  /**
   * VRG-232 — certificate groups. NULL = not part of a group. Set on the parent
   * when it has children, and on every child pointing at its parent. See
   * drizzle/0055_certificate_groups.sql for the full model.
   */
  parent_certificate_id?: UUID | null;
  /**
   * VRG-232 — property of the GROUP, denormalized onto the parent AND every
   * child, immutable after creation. NULL = not part of a group.
   */
  group_divisibility?: CertificateGroupDivisibility | null;
}
